import React from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Button, Row, Col, Card, Alert, Spin, Tooltip, Popconfirm, Badge } from 'antd';
import {
  ArrowLeftOutlined,
  ApartmentOutlined,
  CommentOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  UndoOutlined,
  DeleteOutlined,
  FolderOutlined,
  ToolOutlined,
  UnorderedListOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { usePagoDetalle } from './usePagoDetalle';
import { useSelector } from 'react-redux';
import '../../pagosV2.css';

import Timeline from './Timeline';
import Informacion from './Informacion';
import Autorizaciones from './Autorizaciones';
import Historial from './Historial';
import TablaServicios from './TablaServicios';
import ModalEnviarEmail from './modales/ModalEnviarEmail';
import ModalCargarFactura from './modales/ModalCargarFactura';
import ModalValidarFactura from './modales/ModalValidarFactura';
import ModalCargarComplemento from './modales/ModalCargarComplemento';
import ModalCargarXLSXPago from './modales/ModalCargarXLSXPago';
import ModalCargarPDFPago from './modales/ModalCargarPDFPago';
import ModalCargarDocumentoConcesionado from './modales/ModalCargarDocumentoConcesionado';
import ModalAdminStatusOverride from './modales/ModalAdminStatusOverride';
import DrawerTareasOp from './modales/DrawerTareasOp';
import ModalRechazarOp from './modales/ModalRechazarOp';

const getEstatusTexto = (estatus) => {
  const textos = {
    1: { texto: 'Orden creada, pendiente de validación', type: 'warning' },
    2: { texto: 'Orden validada, lista para solicitar factura', type: 'warning' },
    3: { texto: 'Factura solicitada a proveedor, en espera de documento', type: 'warning' },
    4: { texto: 'Factura recibida, pendiente de validación', type: 'warning' },
    5: { texto: 'Factura validada, pendiente de pago', type: 'warning' },
    6: { texto: 'Factura pagada, en espera de complemento de pago', type: 'warning' },
    7: { texto: 'Proceso completado - Complemento de pago recibido', type: 'success' }
  };
  return textos[estatus] || { texto: 'Estado desconocido', type: 'info' };
};

export default function PagoDetalle() {
  const { ordenPagoID } = useParams();
  const history = useHistory();
  const { email } = useSelector((state) => state.userReducer.user);

  const {
    ordenPago,
    loadingAutorizaciones,
    timelineProps,
    datosPrincipalesProps,
    historialProps,
    tablaServiciosProps,
    modalEmailProps,
    modalFacturaProps,
    modalValidarFacturaProps,
    modalComplementoProps,
    modalXLSXPagoProps,
    modalPDFPagoProps,
    modalTareasProps,
    modalConcesionadoProps,
    modalAdminOverrideProps,
    modalRechazarProps
  } = usePagoDetalle(ordenPagoID);

  const handleVolver = () => {
    history.push('/ordenesPagoV2');
  };

  const estatusInfo = getEstatusTexto(ordenPago?.estatusOrdenPagoID);

  return (
    <div className="pagos-v2 pagos-v2__container">
      <Row align="middle" justify="space-between" style={{ marginBottom: 20 }}>
        <Col>
          <div className="card__titulo" style={{ margin: 0 }}>Orden de Pago: {ordenPagoID}</div>
        </Col>
        <Col>
          <Button
            type="default"
            icon={<ArrowLeftOutlined />}
            onClick={handleVolver}
          >
            Volver al listado
          </Button>
        </Col>
      </Row>

      {loadingAutorizaciones ? (
        <div className="pagos-v2__loading-state">
          <Spin size="large" />
        </div>
      ) : (
        <>
          {ordenPago?.estaRechazada ? (
            <Alert
              message={<span>Operación Rechazada | <strong>Fecha:</strong> {new Date(ordenPago.fechaRechazada).toLocaleDateString()} | <strong>Motivo:</strong> {ordenPago.motivoRechazada}</span>}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
          ) : (
            <Alert
              message={estatusInfo.texto}
              type={estatusInfo.type}
              style={{ marginBottom: 16 }}
            />
          )}

          <Row gutter={[16, 16]} style={{ display: 'flex' }}>
            <Col xs={24} sm={12} lg={6} style={{ display: 'flex' }}>
              <Card
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>
                      <ApartmentOutlined style={{ marginRight: 8 }} />
                      Flujo de la Orden
                    </span>
                    {ordenPago?.estatusOrdenPagoID === 1 ? (
                      <Popconfirm
                        title="¿Eliminar orden de pago?"
                        onConfirm={timelineProps.onEliminarOP}
                        okText="Si, eliminar"
                        cancelText="Cancelar"
                        okButtonProps={{ danger: true }}
                        overlayClassName="popconfirm-rounded"
                      >
                        <Tooltip title="Eliminar orden de pago">
                          <Button
                            danger
                            type="default"
                            size="small"
                            icon={<DeleteOutlined />}
                            loading={timelineProps.loadingEliminar}
                          disabled={ordenPago?.estaRechazada}
                          />
                        </Tooltip>
                      </Popconfirm>
                    ) : ordenPago?.estatusOrdenPagoID >= 2 && (
                      <Tooltip title="Regresar al paso anterior">
                        <Button
                          type="default"
                          size="small"
                          icon={<UndoOutlined />}
                          onClick={timelineProps.onRegresoFlujo}
                          disabled={loadingAutorizaciones || (ordenPago?.fechaPagado && ordenPago.fechaPagado !== "") || ordenPago?.estaRechazada}
                        />
                      </Tooltip>
                    )}
                  </div>
                }
                size="small"
                type="inner"
                style={{ width: '100%' }}
              >
                <Timeline {...timelineProps} />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6} style={{ display: 'flex' }}>
              <Card
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>
                      <FileTextOutlined style={{ marginRight: 8 }} />
                      Información
                    </span>
                    <Popconfirm
                      title={ordenPago?.archivado ? "¿Desarchivar orden de pago?" : "¿Archivar orden de pago?"}
                      description={ordenPago?.archivado ? "Esta orden volverá a estar visible en el listado principal." : "¿Estás seguro de que deseas archivar esta orden de pago?"}
                      onConfirm={datosPrincipalesProps.onArchivar}
                      okText="Sí, continuar"
                      cancelText="Cancelar"
                      okButtonProps={{ danger: !ordenPago?.archivado }}
                      overlayClassName="popconfirm-rounded"
                    >
                      <Tooltip title={ordenPago?.archivado ? "Desarchivar" : "Archivar"}>
                        <Button
                          type={ordenPago?.archivado ? "primary" : "default"}
                          size="small"
                          icon={<FolderOutlined />}
                          loading={datosPrincipalesProps.loadingArchivar}
                        />
                      </Tooltip>
                    </Popconfirm>
                  </div>
                }
                size="small"
                type="inner"
                style={{ width: '100%' }}
              >
                <Informacion {...datosPrincipalesProps} />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6} style={{ display: 'flex' }}>
              <Card
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>
                      <CheckCircleOutlined style={{ marginRight: 8 }} />
                      Autorizaciones
                    </span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {email === 'pat9000@hotmail.com' && (
                        <Tooltip title="Admin Status Override">
                          <Button
                            type="primary"
                            size="small"
                            icon={<ToolOutlined />}
                            onClick={datosPrincipalesProps.onAdminStatusOverride}
                          />
                        </Tooltip>
                      )}
                      <Tooltip title="Rechazar OP">
                        <Button
                          danger
                          size="small"
                          icon={<CloseCircleOutlined />}
                          onClick={datosPrincipalesProps.onRechazarOp}
                          disabled={ordenPago?.estaRechazada}
                        />
                      </Tooltip>
                    </div>
                  </div>
                }
                size="small"
                type="inner"
                style={{ width: '100%' }}
              >
                <Autorizaciones {...datosPrincipalesProps} />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6} style={{ display: 'flex' }}>
              <Card
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', overflow: 'visible' }}>
                    <span>
                      <CommentOutlined style={{ marginRight: 8 }} />
                      Historial
                    </span>
                    <Badge count={historialProps.tareasEnProceso} size="small" offset={[-2, 2]} style={{ marginRight: 10 }}>
                      <Button
                        type="default"
                        size="small"
                        icon={<UnorderedListOutlined />}
                        onClick={historialProps.onAbrirTareas}
                      />
                    </Badge>
                  </div>
                }
                size="small"
                type="inner"
                style={{ width: '100%' }}
              >
                <Historial {...historialProps} />
              </Card>
            </Col>
          </Row>

          <TablaServicios {...tablaServiciosProps} />
        </>
      )}

      <ModalEnviarEmail {...modalEmailProps} />

      {modalFacturaProps.visible && (
        <ModalCargarFactura
          showModal={modalFacturaProps.visible}
          setShowModal={modalFacturaProps.onClose}
          ordenPagoID={modalFacturaProps.ordenPagoID}
          loading={modalFacturaProps.loading}
          setLoading={modalFacturaProps.setLoading}
          onSuccess={modalFacturaProps.onSuccess}
        />
      )}

      <DrawerTareasOp {...modalTareasProps} />

      <ModalValidarFactura {...modalValidarFacturaProps} />

      <ModalCargarComplemento {...modalComplementoProps} />

      <ModalCargarXLSXPago {...modalXLSXPagoProps} />

      <ModalCargarPDFPago {...modalPDFPagoProps} />

      <ModalCargarDocumentoConcesionado {...modalConcesionadoProps} />

      <ModalAdminStatusOverride {...modalAdminOverrideProps} />

      <ModalRechazarOp {...modalRechazarProps} />
    </div>
  );
}
